/**
 * Shared contract context resolution for the add-in.
 * Works with both WOPI mode and SharePoint/Auth mode.
 *
 * Resolution order:
 * 1. Read embedded settings from document (Office.context.document.settings)
 * 2. Extract file ID from document URL and call backend API
 * 3. Fall back to localStorage userdata for tenantId (with retry for race condition)
 */
import { getOfficeDocumentUrl, readContractContextFromDocProps } from './wordApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

export interface ContractContext {
  contractId: string;
  tenantId: string;
  templateId: string;
  templateTypeId?: string;
  templateTypeName?: string;
  contractNumber?: string;
  contractTitle?: string;
  companyId?: string;
  microsoftFileId?: string;
}

/**
 * Read contract context embedded in the document via webextension properties.
 * These are set by EzyReact's embedAddinInDocument() when the document is
 * uploaded to Microsoft (both WOPI and Auth flows).
 */
function readEmbeddedSettings(): ContractContext | null {
  try {
    if (typeof Office === 'undefined' || !Office?.context?.document?.settings) {
      console.log('[contractResolver] Office.context.document.settings not available');
      return null;
    }

    const settings = Office.context.document.settings;
    const contractId = settings.get('ezylex.contractId');
    const tenantId = settings.get('ezylex.tenantId');

    console.log('[contractResolver] Embedded settings:', { contractId, tenantId });

    if (!contractId && !tenantId) return null;

    return {
      contractId: contractId || '',
      tenantId: tenantId || '',
      templateId: settings.get('ezylex.templateId') || '',
      templateTypeId: settings.get('ezylex.templateTypeId') || undefined,
      templateTypeName: settings.get('ezylex.templateTypeName') || undefined,
    };
  } catch (err) {
    console.warn('[contractResolver] Could not read embedded settings:', err);
    return null;
  }
}

/**
 * Extract a Microsoft file ID from a document URL.
 */
function extractFileId(docUrl: string): string | null {
  const wopiMatch = docUrl.match(/\/wopi\/files\/([^/?#]+)/i);
  if (wopiMatch?.[1]) return wopiMatch[1];

  try {
    const url = new URL(docUrl);
    const wopiSrc = url.searchParams.get('WOPISrc');
    if (wopiSrc) {
      const srcMatch = wopiSrc.match(/\/files\/([^/?#]+)/i);
      if (srcMatch?.[1]) return srcMatch[1];
    }
  } catch { /* skip */ }

  const driveItemMatch = docUrl.match(/\/items\/([a-zA-Z0-9!_-]+)/i);
  if (driveItemMatch?.[1]) return driveItemMatch[1];

  try {
    const url = new URL(docUrl);
    const resid = url.searchParams.get('resid');
    if (resid) return resid;
  } catch { /* skip */ }

  return null;
}

/** Get tenantId from localStorage userdata */
function getTenantIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('userdata');
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.tenantId || null;
  } catch {
    return null;
  }
}

/**
 * Whether a document URL belongs to an Ezylex-managed document.
 *
 * Ezylex contracts are served as WOPI files from an Ezylex host
 * (…/wopi/files/<id>). Any document whose URL resolves to a *different* host
 * — OneDrive, SharePoint, personal Word — was NOT opened from Ezylex CLM; the
 * add-in was merely sideloaded onto it. For those we must NOT fall back to the
 * active-edit-session side-channel, which (because the add-in is anonymous)
 * returns the globally most-recent edit session across tenants and would
 * wrongly attach an unrelated contract to a foreign document.
 *
 * An unparseable/absent URL returns false here (not classified as foreign) so
 * genuine Ezylex WOPI docs — whose URL is often opaque/stripped — still reach
 * the side-channel.
 */
function isEzylexDocumentUrl(docUrl: string | null): boolean {
  if (!docUrl) return false;
  if (/\/wopi\/files\//i.test(docUrl)) return true;
  try {
    const host = new URL(docUrl).hostname.toLowerCase();
    return host.endsWith('ezylex.ai') || host.endsWith('drupallers.com');
  } catch {
    return false;
  }
}

/** Wait for userdata to appear in localStorage (handles race condition with App.tsx fetch) */
async function waitForUserdata(maxWaitMs: number = 5000): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    const tenantId = getTenantIdFromStorage();
    if (tenantId) return tenantId;
    await new Promise((r) => setTimeout(r, 500));
  }
  return null;
}

/**
 * Session-scoped cache. The first component to successfully resolve a *full*
 * contract context (contractId + tenantId) wins; later components read the
 * cached value instead of re-running the resolver — which is important
 * because Office.context.document.settings can become unavailable on some
 * navigations and a fresh call would fall through to a partial result.
 */
let cachedContext: ContractContext | null = null;
let inFlight: Promise<ContractContext | null> | null = null;

function hasFullContext(c: ContractContext | null | undefined): boolean {
  return !!(c && c.contractId && c.tenantId);
}

/**
 * Resolve the current contract context from the open document.
 *
 * Uses a module-level cache so the first successful full resolve is reused by
 * subsequent components. Pass { force: true } to bypass cache.
 */
export async function resolveContractContext(opts?: {
  force?: boolean;
}): Promise<ContractContext | null> {
  // The task pane is reused across documents in a Word session, so a cache from
  // a previously-open contract must NOT be served for a different document. The
  // document's own embedded contractId is authoritative and document-specific:
  // if it disagrees with the cache, the pane is now on a different contract —
  // drop the stale cache so we re-resolve. (Cheap, synchronous, no network.)
  const embeddedNow = readEmbeddedSettings();
  if (
    embeddedNow?.contractId &&
    cachedContext?.contractId &&
    embeddedNow.contractId !== cachedContext.contractId
  ) {
    console.log('[contractResolver] Document changed — invalidating cached context');
    cachedContext = null;
    inFlight = null;
  }

  if (!opts?.force && hasFullContext(cachedContext)) {
    return cachedContext;
  }
  if (inFlight && !opts?.force) {
    return inFlight;
  }

  inFlight = (async () => {
    const result = await resolveContractContextInner();
    if (hasFullContext(result)) {
      cachedContext = result;
    } else if (result && !cachedContext) {
      // Cache partial (tenantId-only) only if we don't already have something better.
      cachedContext = result;
    }
    return result;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

async function resolveContractContextInner(): Promise<ContractContext | null> {
  console.log('[contractResolver] Starting resolution...');

  // Strategy 0: Custom document properties, stamped by the WOPI GetFile
  // endpoint onto every Ezylex-served document. This is the authoritative,
  // document-specific signal — readable via Word.run regardless of URL or
  // add-in id, and absent on foreign (OneDrive/SharePoint) documents.
  const fromDocProps = await readContractContextFromDocProps();
  if (fromDocProps?.contractId && fromDocProps?.tenantId) {
    console.log('[contractResolver] ✅ Resolved from custom document properties');
    return {
      contractId: fromDocProps.contractId,
      tenantId: fromDocProps.tenantId,
      templateId: '',
    };
  }

  // Strategy 1: Read embedded settings from the document (webextension props)
  const embedded = readEmbeddedSettings();
  if (embedded?.contractId && embedded?.tenantId) {
    console.log('[contractResolver] ✅ Resolved from embedded document settings');
    return embedded;
  }

  // Resolve the open document's URL once. It drives both the contract lookup
  // (Strategy 2) and the foreign-document check that gates the side-channel
  // (Strategy 3).
  let docUrl: string | null = null;
  try {
    docUrl = await getOfficeDocumentUrl();
  } catch {
    docUrl = null;
  }
  console.log('[contractResolver] Document URL:', docUrl);
  const foreignDocument = !!docUrl && !isEzylexDocumentUrl(docUrl);
  if (foreignDocument) {
    console.log(
      '[contractResolver] Foreign (non-Ezylex) document detected — skipping backend/side-channel lookups'
    );
  }

  // Strategy 2: Extract file ID from document URL and call backend
  try {
    if (docUrl && !foreignDocument) {
      const fileId = extractFileId(docUrl);
      console.log('[contractResolver] Extracted fileId:', fileId);

      const resolveUrl = fileId
        ? `${API_BASE_URL}/api/addin/contract-by-file-id-public?fileId=${encodeURIComponent(fileId)}`
        : `${API_BASE_URL}/api/addin/contract-by-file-id-public?fileUrl=${encodeURIComponent(docUrl)}`;

      const response = await fetch(resolveUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'x-addin-token': API_TOKEN,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.contractId) {
          console.log('[contractResolver] ✅ Resolved from document URL + backend');
          return data as ContractContext;
        }
      }
    }
  } catch (err) {
    console.warn('[contractResolver] URL resolution failed:', err);
  }

  // NOTE: The old active-edit-session side-channel (Strategy 3) has been
  // removed. Because the add-in is anonymous, it did a cross-tenant
  // "globally most-recent edit session" lookup that bound an unrelated
  // contract to any document with no local Ezylex context — including
  // foreign OneDrive/SharePoint documents (observed: it attached
  // "Marketing_Partnership_Agreement" to an unrelated OneDrive NDA). Genuine
  // contracts now resolve document-specifically via custom document
  // properties (Strategy 0), so the side-channel is no longer needed and its
  // cross-tenant leak is closed.

  // Strategy 4: Use embedded settings with partial data (tenantId only)
  if (embedded?.tenantId) {
    console.log('[contractResolver] ✅ Using partial embedded settings (tenantId only)');
    return embedded;
  }

  // Strategy 5: Get tenantId from localStorage userdata (with retry for race condition)
  console.log('[contractResolver] Trying localStorage fallback (waiting for userdata)...');
  const tenantId = getTenantIdFromStorage() || await waitForUserdata(5000);

  if (tenantId) {
    console.log('[contractResolver] ✅ Resolved tenantId from localStorage:', tenantId);
    return { contractId: '', tenantId, templateId: '' };
  }

  console.warn('[contractResolver] ❌ Could not resolve contract context');
  return null;
}
